import { useEffect } from 'react';
import { useSetRecoilState } from 'recoil';
import { agentCustomSqlTestConfigPlan } from '../../../../atoms/atoms';
import type {
  AgentCustomSqlTestConfigChange,
  AgentTestOperation,
  PlanApprovalStatus,
} from '../../../../types';
import { pluralizeString } from '../../../../utils/common';
import {
  formatCustomSqlTestChangeLabel,
  formatCustomSqlTestChangeSummary,
  getCustomSqlPlanActionLabel,
  withCustomSqlChangeIds,
} from '../../../../utils/customSqlTestConfig';
import type { ApprovalToolRendererProps } from '../../components/ApprovalToolRenderer';
import { parseCodeTestPlanResult } from '../ApplyCodeTestConfigPlanToolRenderer/utils';
import { createPlanApprovalRenderer } from '../applyTestConfigPlanShared/ApplyTestConfigPlanToolRenderer';
import { TEST_MANAGEMENT_PATH } from '../applyTestConfigPlanShared/sharedUtils';

const PREVIEW_PATH = `${TEST_MANAGEMENT_PATH}/custom-sql-agent-preview`;

const getHeaderLabel = (
  status: PlanApprovalStatus | undefined,
  operation: AgentTestOperation,
  count: number,
): string => {
  if (status === 'confirmed') return 'Plan approved';
  if (status === 'rejected') return 'Plan rejected';
  const tests = pluralizeString(count, 'custom SQL test', 'custom SQL tests');
  return operation === 'delete' ? `Delete ${tests}` : `Add ${tests}`;
};

const getApproveLabel = (
  _operation: AgentTestOperation,
  changes: AgentCustomSqlTestConfigChange[],
  isCustomSqlCloudEnabled?: boolean,
) => getCustomSqlPlanActionLabel(changes, !!isCustomSqlCloudEnabled);

const BaseRenderer = createPlanApprovalRenderer<AgentCustomSqlTestConfigChange>(
  {
    planAtom: agentCustomSqlTestConfigPlan,
    previewPath: PREVIEW_PATH,
    returnPath: TEST_MANAGEMENT_PATH,
    formatChangeLabel: formatCustomSqlTestChangeLabel,
    formatChangeSummary: formatCustomSqlTestChangeSummary,
    getHeaderLabel,
    getApproveLabel,
    normalizeChanges: withCustomSqlChangeIds,
  },
);

const ApplyCustomSqlTestConfigPlanToolRendererInner = (
  props: ApprovalToolRendererProps,
) => {
  const setPlan = useSetRecoilState(agentCustomSqlTestConfigPlan);
  const toolCallId = props.invocation.toolCallId;
  const draftPr = parseCodeTestPlanResult(
    props.invocation.result?.result,
  )?.draftPr;

  useEffect(() => {
    if (!draftPr) return;
    setPlan((prev) => {
      if (!prev || prev.toolCallId !== toolCallId || prev.draftPr) return prev;
      return { ...prev, draftPr };
    });
  }, [draftPr, setPlan, toolCallId]);

  return <BaseRenderer {...props} />;
};

// Called as a plain function by ToolInvocationRenderer rather than as JSX, so
// this wrapper must not use hooks — only return an element that owns them.
const ApplyCustomSqlTestConfigPlanToolRenderer = (
  props: ApprovalToolRendererProps,
) => <ApplyCustomSqlTestConfigPlanToolRendererInner {...props} />;

export default ApplyCustomSqlTestConfigPlanToolRenderer;
